import React, { useCallback } from "react";
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Podcasts as PodcastsIcon,
  AdminPanelSettings as AdminIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Assessment as AnalysisIcon,
  Help as HelpIcon,
  Instagram as InstagramIcon,
  TrackChanges as TrackerIcon,
  ArticleOutlined as ArticleOutlinedIcon,
  AutoMode as PipelinesIcon,
  Email as EmailIcon,
  Collections as CollectionsIcon,
  Campaign as CampaignIcon,
  MovieFilter as ClipsIcon,
  ChatBubbleOutline as ChatIcon,
  VideoLibrary as ChannelsIcon,
  // AccountBalance as EleccionesIcon,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { alpha } from "@mui/material/styles";
import { ROUTES_NAMES } from "./routes";
import { canAccessCampaigns } from "../campaigns/campaignAccess";
import { canAccessClipsCompany } from "../pages/clips/clipsAccess";
import { themeConstants } from "../theme";
import { resolveMiContenidoDateRange } from "../miContenido/resolveMiContenidoDateRange";
import { useProfileStore } from "../stores/profileStore";
import {
  canAccessExplorerPanel,
  EXPLORER_PANEL_TAB,
  getActiveExplorerTab,
  getActiveExplorerPillar,
  getExplorerSidebarGroups,
  getExplorerSidebarTabs,
  isExplorerCanalesHubPath,
  isExplorerSidebarChildActive,
} from "../pages/Explorer/explorerTabs";
import { useExplorerClipsIa } from "../pages/Explorer/useExplorerClipsIa";
import { useExplorerStore } from "../stores/explorerStore";
import { apiService } from "../services/api";
import { getLastChatId } from "../services/lastChat";
import { useChatAccess } from "../pages/Chat/useChatAccess";
import { PROFILE_GC_MS, PROFILE_STALE_MS } from "../pages/profileQueryConfig";
import { productBrand } from "../productBrand";
import { useTranslation } from "react-i18next";
import LanguageToggle from "./LanguageToggle";
import { useLocale } from "../i18n/LocaleProvider";
import ProductWordmark from "./ProductWordmark";

interface SidebarProps {
  mobileOpen?: boolean;
  handleDrawerToggle?: () => void;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  isActive: boolean;
  isExternal?: boolean;
  nested?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
}

export const DRAWER_WIDTH = 168;
export const NAVBAR_HEIGHT = 64;

const ONCE_STREAM_COMPANY_ID = "69d7ce5457158760de55e648";
const MANUALES_NOTION_URL =
  "https://www.notion.so/Manuales-Once-Stream-3578f617296b80aa9dffd4bf44ed60b5";
const PODSCOPE_GUIAS_NOTION_URL =
  "https://polished-minute-aa1.notion.site/Gu-as-Podscope-2628f617296b803f907ad12b3aa4e023";
const DEFAULT_SIDEBAR_GUIDES_LABEL = productBrand.guidesLabel;
const PRODUCT_WORDMARK_HEIGHT = "1.5rem";

const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen,
  handleDrawerToggle,
}) => {
  const location = useLocation();
  const { t } = useTranslation("common");
  const { t: tExplorer } = useTranslation("explorer");
  const { publicSurface } = useLocale();
  const queryClient = useQueryClient();
  const {
    isAuthenticated,
    signOut,
    user,
    isAdmin: isPlatformAdmin,
    hasProductAccess,
    hasAnyExplorerAccess,
    hasExplorerTabAccess,
    company,
    getMiContenidoDateFilterMinStartDate,
    getMiContenidoDateFilterMaxEndDate,
  } = useAuth();
  const { access: chatAccess } = useChatAccess();
  const { dateMode, presetDays, customStartDate, customEndDate } =
    useProfileStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isMiContenidoDemo = location.pathname.startsWith(
    ROUTES_NAMES.MI_CONTENIDO_DEMO,
  );
  const isChat = location.pathname.startsWith(ROUTES_NAMES.CHAT);
  const isExplorar = location.pathname.startsWith(ROUTES_NAMES.EXPLORAR);
  const isPodcasts = location.pathname.startsWith(ROUTES_NAMES.PODCASTS);
  const isMisPodcasts = location.pathname.startsWith(ROUTES_NAMES.MIS_PODCASTS);
  const isMiContenido = location.pathname.startsWith(ROUTES_NAMES.MI_CONTENIDO);
  const isSettings = location.pathname.startsWith(ROUTES_NAMES.SETTINGS);
  const isAdminPath = location.pathname.startsWith(ROUTES_NAMES.ADMIN);
  const isChannels =
    location.pathname === ROUTES_NAMES.CHANNELS ||
    location.pathname.startsWith(`${ROUTES_NAMES.CHANNELS}/`);
  const isElecciones = location.pathname.startsWith(ROUTES_NAMES.ELECCIONES);
  const isBrandAnalysis = location.pathname.startsWith(
    ROUTES_NAMES.BRAND_ANALYSIS,
  );
  const isPodcastAnalysis = location.pathname.startsWith(
    ROUTES_NAMES.PODCAST_ANALYSIS,
  );
  const isProgramAnalysis = location.pathname.startsWith(
    ROUTES_NAMES.PROGRAM_ANALYSIS,
  );
  const isProgramAnalysisLimited = location.pathname.startsWith(
    ROUTES_NAMES.PROGRAM_ANALYSIS_LIMITED,
  );
  const isSocialAdmin = location.pathname.startsWith(ROUTES_NAMES.SOCIAL_ADMIN);
  const isExplorer = location.pathname.startsWith(ROUTES_NAMES.EXPLORER);
  const isTracker = location.pathname.startsWith(ROUTES_NAMES.TRACKER);

  const isClips = location.pathname.startsWith(ROUTES_NAMES.CLIPS);
  const isCampanas = location.pathname.startsWith(ROUTES_NAMES.CAMPANAS);
  const clipsIa = useExplorerClipsIa();
  const setSocialViewMode = useExplorerStore((s) => s.setSocialViewMode);

  const canAccessMiContenido =
    isPlatformAdmin || hasProductAccess("micontenido");
  const canAccessClips =
    hasProductAccess("clips") && canAccessClipsCompany(user?.companyId);
  const canShowCampanas =
    canAccessMiContenido && canAccessCampaigns(user?.companyId);

  const handleMiContenidoPrefetch = useCallback(() => {
    if (!canAccessMiContenido || !user?.companyId) return;
    const profileScopeCacheKey =
      user.companyId ?? `podcasts:${(user.podcastIds ?? []).join(",")}`;
    const minBound = getMiContenidoDateFilterMinStartDate();
    const maxBound = getMiContenidoDateFilterMaxEndDate();
    const { startDate, endDate } = resolveMiContenidoDateRange({
      dateMode,
      presetDays,
      customStartDate,
      customEndDate,
      userRole: user.role,
      company,
      user,
      minBound,
      maxBound,
    });
    queryClient.prefetchQuery({
      queryKey: [
        "mi-contenido-dashboard",
        profileScopeCacheKey,
        startDate,
        endDate,
        undefined,
      ],
      queryFn: () =>
        apiService.getMiContenidoDashboard(startDate, endDate, undefined, {
          includeRankingStats: false,
        }),
      staleTime: PROFILE_STALE_MS,
      gcTime: PROFILE_GC_MS,
    });
  }, [
    canAccessMiContenido,
    user,
    company,
    dateMode,
    presetDays,
    customStartDate,
    customEndDate,
    getMiContenidoDateFilterMinStartDate,
    getMiContenidoDateFilterMaxEndDate,
    queryClient,
  ]);

  if (
    !isChat &&
    !isExplorar &&
    !isPodcasts &&
    !isMisPodcasts &&
    !isMiContenido &&
    !isSettings &&
    !isAdminPath &&
    !isChannels &&
    !isElecciones &&
    !isBrandAnalysis &&
    !isPodcastAnalysis &&
    !isProgramAnalysis &&
    !isProgramAnalysisLimited &&
    !isSocialAdmin &&
    !isExplorer &&
    !isTracker &&
    !isClips &&
    !isCampanas
  ) {
    return null;
  }

  const isOnceStreamCompany = user?.companyId === ONCE_STREAM_COMPANY_ID;
  const sidebarGuidesUrl =
    company?.sidebarGuidesUrl?.trim() ||
    (isOnceStreamCompany ? MANUALES_NOTION_URL : PODSCOPE_GUIAS_NOTION_URL);
  const sidebarGuidesLabel =
    company?.sidebarGuidesLabel?.trim() ||
    (isOnceStreamCompany ? "Manuales" : DEFAULT_SIDEBAR_GUIDES_LABEL);
  const helpGuidesMenuItem: MenuItem = {
    text: sidebarGuidesLabel,
    icon: <HelpIcon />,
    path: sidebarGuidesUrl,
    isActive: false,
    isExternal: true,
  };

  const canAccessChat = Boolean(chatAccess?.allowed);
  const lastChatId = getLastChatId(user?._id);
  const chatMenuItem: MenuItem = {
    text: "Chat",
    icon: <ChatIcon />,
    // Reopens the last chat from other pages; inside Chat it goes to the chat home.
    path:
      lastChatId && !isChat
        ? `${ROUTES_NAMES.CHAT}/${lastChatId}`
        : ROUTES_NAMES.CHAT,
    isActive: isChat,
  };

  const miContenidoMenuItem: MenuItem = {
    text: t("nav.myContent"),
    icon: <CollectionsIcon />,
    path: ROUTES_NAMES.MI_CONTENIDO,
    isActive:
      location.pathname.startsWith(ROUTES_NAMES.MI_CONTENIDO) &&
      !(canShowCampanas && isCampanas),
    onMouseEnter: handleMiContenidoPrefetch,
  };

  const activeExplorerTab = getActiveExplorerTab(location.pathname);
  const activeExplorerPillar = getActiveExplorerPillar(location.pathname, clipsIa);
  const canShowExplorer = !isMiContenidoDemo && hasAnyExplorerAccess();
  const explorerSidebarGroups = canShowExplorer
    ? getExplorerSidebarGroups(hasExplorerTabAccess, clipsIa)
    : [];
  const panelMenuItems: MenuItem[] =
    canShowExplorer && canAccessExplorerPanel(hasExplorerTabAccess)
      ? [
          {
            text: tExplorer(EXPLORER_PANEL_TAB.labelKey),
            icon: <EXPLORER_PANEL_TAB.icon />,
            path: EXPLORER_PANEL_TAB.path,
            isActive:
              isExplorer && activeExplorerTab === EXPLORER_PANEL_TAB.index,
          },
        ]
      : [];
  const explorerMenuItems: MenuItem[] = canShowExplorer
    ? getExplorerSidebarTabs(hasExplorerTabAccess, clipsIa).map((tab) => {
          const TabIcon = tab.icon;
          return {
            text: tExplorer(tab.labelKey),
            icon: <TabIcon />,
            path: tab.path,
            isActive: isExplorer && activeExplorerPillar === tab.index,
          };
        })
    : [];
  const explorerNestedMenu = explorerSidebarGroups.length > 0;

  /** Screenshot demo: only Mi contenido — hide Campañas, Guías, admin, etc. */
  const mainMenuItems: MenuItem[] = isMiContenidoDemo
    ? canAccessMiContenido
      ? [miContenidoMenuItem]
      : []
    : [
    ...panelMenuItems,
    ...(canAccessChat ? [chatMenuItem] : []),
    ...(canAccessMiContenido ? [miContenidoMenuItem] : []),
    ...(hasProductAccess("mispodcasts")
      ? [
          {
            text: t("nav.myPodcasts"),
            icon: <PodcastsIcon />,
            path: ROUTES_NAMES.MIS_PODCASTS,
            isActive: location.pathname.includes(ROUTES_NAMES.MIS_PODCASTS),
          },
        ]
      : []),
    ...(canShowCampanas
      ? [
          {
            text: t("nav.campaigns"),
            icon: <CampaignIcon />,
            path: ROUTES_NAMES.CAMPANAS,
            isActive: isCampanas,
          },
        ]
      : []),
    ...(canAccessClips
      ? [
          {
            text: "Clips",
            icon: <ClipsIcon />,
            path: ROUTES_NAMES.CLIPS,
            isActive: location.pathname.startsWith(ROUTES_NAMES.CLIPS),
          },
        ]
      : []),
    ...(hasProductAccess("tracker")
      ? [
          {
            text: "Tracker",
            icon: <TrackerIcon />,
            path: ROUTES_NAMES.TRACKER,
            isActive: location.pathname.startsWith(ROUTES_NAMES.TRACKER),
          },
        ]
      : []),
    ...(user?._id === "6914dccdb3291e1d80ebfe0a"
      ? [
          {
            text: t("nav.explore"),
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.PROGRAM_ANALYSIS_LIMITED,
            isActive: location.pathname.includes(
              ROUTES_NAMES.PROGRAM_ANALYSIS_LIMITED,
            ),
          },
        ]
      : []),
  ];

  const bottomMenuItems: MenuItem[] = isMiContenidoDemo
    ? []
    : [
        ...(user?.companyRole === "admin"
          ? [
              {
                text: t("nav.settings"),
                icon: <SettingsIcon />,
                path: ROUTES_NAMES.SETTINGS,
                isActive: location.pathname.includes(ROUTES_NAMES.SETTINGS),
              },
            ]
          : []),
        ...(user?._id !== "6914dccdb3291e1d80ebfe0a"
          ? [helpGuidesMenuItem]
          : []),
      ];

  const authMenuItem: MenuItem = isAuthenticated
    ? {
        text: t("nav.signOut"),
        icon: <LogoutIcon />,
        path: "",
        isActive: false,
        onClick: signOut,
      }
    : {
        text: "Login",
        icon: <LoginIcon />,
        path: "/auth",
        isActive: false,
      };

  const adminMenuItems: MenuItem[] =
    !isMiContenidoDemo && user?.role === "admin"
      ? [
          {
            text: "Admin",
            icon: <AdminIcon />,
            path: ROUTES_NAMES.ADMIN,
            isActive: location.pathname.includes(ROUTES_NAMES.ADMIN),
          },
          {
            text: "Review",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.PODCAST_ANALYSIS,
            isActive: location.pathname.includes(ROUTES_NAMES.PODCAST_ANALYSIS),
          },
          {
            text: "Review audit",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.ADMIN_REVIEW_AUDIT,
            isActive: location.pathname === ROUTES_NAMES.ADMIN_REVIEW_AUDIT,
          },
          {
            text: "Review audit cases",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.ADMIN_REVIEW_AUDIT_CASES,
            isActive: location.pathname === ROUTES_NAMES.ADMIN_REVIEW_AUDIT_CASES,
          },
          {
            text: "Chat usage",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.ADMIN_CHAT_USAGE,
            isActive: location.pathname === ROUTES_NAMES.ADMIN_CHAT_USAGE,
          },
          {
            text: "Marcas",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.BRAND_ANALYSIS,
            isActive: location.pathname.startsWith(
              ROUTES_NAMES.BRAND_ANALYSIS,
            ),
          },
          {
            text: "Rankings",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.PROGRAM_ANALYSIS,
            isActive: location.pathname === ROUTES_NAMES.PROGRAM_ANALYSIS,
          },
          {
            text: "Análisis de programas",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.ADMIN_PROGRAM_ANALYSIS,
            isActive: location.pathname.startsWith(
              ROUTES_NAMES.ADMIN_PROGRAM_ANALYSIS,
            ),
          },
          {
            text: "Análisis de Canales",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.CHANNEL_ANALYSIS,
            isActive: location.pathname === ROUTES_NAMES.CHANNEL_ANALYSIS,
          },
          {
            text: "Channels",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.CHANNELS,
            isActive:
              location.pathname === ROUTES_NAMES.CHANNELS ||
              location.pathname.startsWith(`${ROUTES_NAMES.CHANNELS}/`),
          },
          {
            text: "Learnings Explorer",
            icon: <AnalysisIcon />,
            path: ROUTES_NAMES.PODCAST_LEARNINGS_EXPLORER,
            isActive:
              location.pathname === ROUTES_NAMES.PODCAST_LEARNINGS_EXPLORER,
          },
          {
            text: "Empresas Prixi",
            icon: <ArticleOutlinedIcon />,
            path: ROUTES_NAMES.ADMIN_PRIXI_COMPANIES,
            isActive: location.pathname.startsWith(
              ROUTES_NAMES.ADMIN_PRIXI_COMPANIES,
            ),
          },
          {
            text: "Social",
            icon: <InstagramIcon />,
            path: ROUTES_NAMES.SOCIAL_ADMIN,
            isActive: location.pathname.includes(ROUTES_NAMES.SOCIAL_ADMIN),
          },
          {
            text: "Pipelines",
            icon: <PipelinesIcon />,
            path: ROUTES_NAMES.ADMIN_PIPELINES,
            isActive: location.pathname.includes(ROUTES_NAMES.ADMIN_PIPELINES),
          },
          {
            text: "Emailing",
            icon: <EmailIcon />,
            path: ROUTES_NAMES.ADMIN_EMAILING,
            isActive: location.pathname === ROUTES_NAMES.ADMIN_EMAILING,
          },
          {
            text: "Reportes métricas",
            icon: <EmailIcon />,
            path: ROUTES_NAMES.ADMIN_METRICS_REPORTS,
            isActive: location.pathname === ROUTES_NAMES.ADMIN_METRICS_REPORTS,
          },
        ]
      : [];

  const renderMenuItem = (item: MenuItem, key = item.text) => (
    <Button
      key={key}
      onMouseEnter={item.onMouseEnter}
      {...(item.isExternal
        ? {
            onClick: () => {
              window.open(item.path, "_blank", "noopener,noreferrer");
              if (isMobile && handleDrawerToggle) {
                handleDrawerToggle();
              }
            },
            component: "button",
          }
        : item.path
        ? {
            component: Link,
            to: item.path,
            onClick: () => {
              item.onClick?.();
              if (isMobile && handleDrawerToggle) {
                handleDrawerToggle();
              }
            },
          }
        : {
            onClick: () => {
              item.onClick?.();
              if (isMobile && handleDrawerToggle) {
                handleDrawerToggle();
              }
            },
            component: "button",
          })}
      startIcon={item.icon ?? undefined}
      variant="text"
      color="inherit"
      fullWidth
      sx={(theme) => ({
        justifyContent: "flex-start",
        borderRadius: themeConstants.border.radius,
        fontWeight: item.isActive ? 600 : 500,
        py: item.nested ? 0.45 : 0.75,
        px: 1.5,
        minWidth: 0,
        fontSize: item.nested ? "0.75rem" : "0.8125rem",
        whiteSpace: "nowrap",
        "& .MuiButton-startIcon": { mr: 1 },
        "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 18 },
        transition: "all 100ms ease",
        color: item.isActive ? "text.primary" : "text.secondary",
        ...(item.isActive
          ? {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.common.white, 0.1)
                  : alpha(theme.palette.primary.main, 0.14),
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.14)
                    : alpha(theme.palette.primary.main, 0.2),
              },
            }
          : {
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }),
        "&.MuiButton-text": {
          px: item.nested ? 1 : 1.5,
        },
      })}
    >
      <Box
        component="span"
        title={item.text}
        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {item.text}
      </Box>
    </Button>
  );

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          component={Link}
          to="/"
          onClick={() => {
            if (isMobile && handleDrawerToggle) {
              handleDrawerToggle();
            }
          }}
          sx={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            lineHeight: 0,
          }}
        >
          <ProductWordmark height={PRODUCT_WORDMARK_HEIGHT} />
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ mr: -1 }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Navigation Items */}
      <Box sx={{ flexGrow: 1, p: 1, overflowY: "auto" }}>
        <Stack spacing={1}>{mainMenuItems.map((item) => renderMenuItem(item))}</Stack>
        {(explorerNestedMenu || explorerMenuItems.length > 0) && (
          <>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", px: 1.5, mt: 2, mb: 0.25, lineHeight: 2 }}
            >
              {tExplorer("section")}
            </Typography>
            {explorerNestedMenu ? (
              <Stack spacing={1}>
                {explorerSidebarGroups.map((group) => {
                  const GroupIcon =
                    group.labelKey === "tabs.sponsors"
                      ? CampaignIcon
                      : ChannelsIcon;
                  const isCanalesGroup = group.labelKey === "tabs.channels";
                  const groupActive =
                    isExplorer &&
                    ((isCanalesGroup &&
                      isExplorerCanalesHubPath(location.pathname)) ||
                      group.children.some((child) =>
                        isExplorerSidebarChildActive(child, location.pathname),
                      ));
                  const groupHeaderSx = {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.5,
                    minHeight: 32,
                    color: groupActive ? "text.primary" : "text.secondary",
                    ...(isCanalesGroup
                      ? {
                          borderRadius: themeConstants.border.radius,
                          textDecoration: "none",
                          width: "100%",
                          boxSizing: "border-box" as const,
                          "&:hover": {
                            bgcolor: (theme: { palette: { mode: string; common: { white: string }; primary: { main: string } } }) =>
                              theme.palette.mode === "dark"
                                ? alpha(theme.palette.common.white, 0.06)
                                : alpha(theme.palette.primary.main, 0.08),
                          },
                        }
                      : {}),
                  };
                  return (
                    <Box key={group.index}>
                      {isCanalesGroup ? (
                        <Box
                          component={Link}
                          to={ROUTES_NAMES.EXPLORER_CANALES}
                          onClick={() => {
                            if (isMobile && handleDrawerToggle) {
                              handleDrawerToggle();
                            }
                          }}
                          sx={groupHeaderSx}
                        >
                          <GroupIcon sx={{ fontSize: 18, opacity: 0.72 }} />
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              lineHeight: 1.2,
                              letterSpacing: 0,
                            }}
                          >
                            {tExplorer(group.labelKey)}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={groupHeaderSx}>
                          <GroupIcon sx={{ fontSize: 18, opacity: 0.72 }} />
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              lineHeight: 1.2,
                              letterSpacing: 0,
                            }}
                          >
                            {tExplorer(group.labelKey)}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{
                          ml: 2.25,
                          pl: 0.75,
                          borderLeft: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Stack spacing={0.25}>
                          {group.children.map((child) => {
                            const isActive =
                              isExplorer &&
                              isExplorerSidebarChildActive(
                                child,
                                location.pathname,
                              );
                            return renderMenuItem(
                              {
                                text: tExplorer(child.labelKey),
                                icon: null,
                                path: child.path,
                                isActive,
                                nested: true,
                                onClick:
                                  child.format === "clips" &&
                                  child.path === ROUTES_NAMES.EXPLORER_CANALES_CLIPS
                                    ? () => {
                                        setSocialViewMode("ranking");
                                      }
                                    : undefined,
                              },
                              `${group.index}-${child.format}`,
                            );
                          })}
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Stack spacing={1}>{explorerMenuItems.map((item) => renderMenuItem(item))}</Stack>
            )}
          </>
        )}
        {adminMenuItems.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", px: 1.5, mb: 1 }}
            >
              Admin
            </Typography>
            <Stack spacing={1}>{adminMenuItems.map((item) => renderMenuItem(item))}</Stack>
          </>
        )}
      </Box>

      {/* Auth Section */}
      <Box
        sx={{
          p: 1,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={1}>
          {!publicSurface ? <LanguageToggle /> : null}
          {[...bottomMenuItems, authMenuItem].map((item) => renderMenuItem(item))}
        </Stack>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
